/**
 * The shape of the global mobile-shell's bootstrap script installs before this page's content
 * loads — see `@sunflower-games/webview-bridge`'s `InjectedBridge` (its `web.ts`). Hand-mirrored
 * rather than sent through `getShellBridge()`: the package is a dependency (`^0.13.0`), but no
 * published version carries the `ui.ready` message type yet — it ships with mobile-shell#21 (0.15.0;
 * 0.14.0 was published for `review.request`). Swapping to `getShellBridge()?.send('ui.ready', {})` then is a
 * one-line change plus a bump to `^0.15.0`, since on 0.x `^0.13.0` stops below 0.14.0.
 */
interface InjectedCrownBridge {
  /**
   * Explicit `false` means the shell is present but its allow-list refuses this origin — `undefined`
   * is a shell that decided the origin at install time and never installed the global if it refused.
   * Either way, `post` reaching a shell it did not mean for is harmless: the shell drops it, this
   * function is fire-and-forget, and skipping it here is a courtesy, not a requirement.
   */
  enabled?: boolean;
  post(type: string, payload: unknown, id?: string): void;
}

const INJECTED_GLOBAL_NAME = '__CROWN_BRIDGE__';

function getInjectedBridge(): InjectedCrownBridge | null {
  const candidate = (window as unknown as Record<string, InjectedCrownBridge | undefined>)[INJECTED_GLOBAL_NAME];

  return typeof candidate?.post === 'function' ? candidate : null;
}

/**
 * Tells mobile-shell this page has painted real content, so it can drop its own loading UI over
 * the page's — see `NativeLanding` and `Lobby`, which call this on `/` once each has something worth
 * showing, and `useShellUiReady`, which covers every other route. A no-op in a plain browser (no shell) or on a refused origin
 * (`enabled === false`); safe to call more than once, since the shell's own handler is idempotent.
 */
export function postUiReady(): void {
  const bridge = getInjectedBridge();

  if (!bridge || bridge.enabled === false) {
    return;
  }

  bridge.post('ui.ready', {});
}
